import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Grid } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";

import { FleetContext, FleetSettingsContext } from "../../context";
import { KeyValueDisplay } from "../../components/Typography";
import { StringUtils } from "../../util";
import { PermissionedDisplay } from "../../components/Layout";
import FleetSettingsSection from "../../components/Layout/FleetSettingsSection";
import Permissions from "../../permissions";
import { FLEET_GROUP_CHOICES, PLATFORM_LABELS } from "../../constants";

const SettingsGeneral = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { activeFleet } = useContext(FleetContext);
  const {
    geofencePackages,
    firmwarePackages,
    fetchFirmwarePackages,
    fetchGeofencePackages,
    markets,
    fetchMarkets,
  } = useContext(FleetSettingsContext);

  useEffect(() => {
    Promise.all([fetchFirmwarePackages(), fetchGeofencePackages(), fetchMarkets()]);
  }, []);

  const getOperatingHoursStr = () => {
    if (
      !activeFleet ||
      !activeFleet.operating_hours_begin ||
      !activeFleet.operating_hours_soft_end ||
      !activeFleet.timezone
    )
      return t("Fleet.Settings.General.24hr");

    return `${StringUtils.convertTz(
      activeFleet.operating_hours_begin,
      "UTC",
      activeFleet.timezone,
      "TIME_SIMPLE",
    )} – ${StringUtils.convertTz(
      activeFleet.operating_hours_soft_end,
      "UTC",
      activeFleet.timezone,
      "TIME_SIMPLE",
    )} ${activeFleet.timezone}`;
  };

  const getRiderSafetyQuizStr = () => {
    if (!activeFleet?.required_survey_template_uuid) return t("Fleet.Settings.General.None");
    const rides = activeFleet?.required_survey_ride_count;
    const days = activeFleet?.required_survey_refresh_days;
    if (!activeFleet?.survey_enabled) return `${t("Fleet.Settings.General.Disabled")}`;
    if (rides && days)
      return `${t("Fleet.Settings.General.Every")} ${rides} ${t(
        "Fleet.Settings.General.rides",
      )} ${t("Fleet.Settings.General.orEvery")} ${days} ${t("Fleet.Settings.General.days")}`;
    if (rides)
      return `${t("Fleet.Settings.General.Every")} ${rides} ${t("Fleet.Settings.General.rides")}`;
    if (days)
      return `${t("Fleet.Settings.General.Every")} ${days} ${t("Fleet.Settings.General.days")}`;
    return t("Fleet.Settings.General.Once");
  };

  const getFirstSurveyOffsetString = () => {
    const firstSurveyOffsetIndex = activeFleet.first_survey_offset;
    return t(`Fleet.Settings.GeneralEdit.FirstSurveyOffset${firstSurveyOffsetIndex}`);
  };

  const getDefaultFirmwarePackageStr = () => {
    const pkg = firmwarePackages.find((x) => x.url === activeFleet.default_firmware_package);
    return pkg ? pkg.version : "(none selected)";
  };

  const getDefaultGeofencePackageStr = () => {
    const pkg = geofencePackages.find((x) => x.url === activeFleet.default_geofence_package);
    return pkg ? pkg.version : "(none selected)";
  };

  const getMarketNameFromUrl = (url) => {
    const market = markets.find((m) => m.url === url);
    return market?.name;
  };

  const getFleetGroupLabel = (val) => FLEET_GROUP_CHOICES[val] || "";

  return (
    <Grid item container spacing={2} style={{ padding: theme.spacing(1, 2, 2, 2) }}>
      <FleetSettingsSection
        heading={t("Fleet.Settings.General.Operations")}
        item
        container
        spacing={2}
      >
        <Grid item xs={12}>
          <KeyValueDisplay
            label={t("Fleet.Settings.General.Market")}
            text={
              activeFleet?.market
                ? getMarketNameFromUrl(activeFleet.market)
                : t("Fleet.Settings.General.None")
            }
            labelMinWidth="25%"
          />
        </Grid>
        <Grid item xs={12}>
          <KeyValueDisplay
            label={t("Fleet.Settings.General.FleetGroup")}
            text={getFleetGroupLabel(activeFleet?.fleet_group) || t("Fleet.Settings.General.None")}
            labelMinWidth="25%"
          />
        </Grid>
        <Grid item xs={12}>
          <KeyValueDisplay
            label={t("Fleet.Settings.General.Platform")}
            text={
              PLATFORM_LABELS[activeFleet?.platform] || activeFleet?.platform || "Superpedestrian"
            }
            labelMinWidth="25%"
          />
        </Grid>
        <Grid item xs={12}>
          <KeyValueDisplay
            label={t("Fleet.Settings.General.Timezone")}
            text={activeFleet?.timezone}
            labelMinWidth="25%"
          />
        </Grid>
        <Grid item xs={12}>
          <KeyValueDisplay
            label={t("Fleet.Settings.General.OperatingHours")}
            text={getOperatingHoursStr()}
            labelMinWidth="25%"
            noWrap={false}
          />
        </Grid>
        <Grid item xs={12}>
          <KeyValueDisplay
            label={t("Fleet.Settings.General.CurfewWarning")}
            text={
              activeFleet?.warn_soft_end
                ? `${activeFleet?.warn_soft_end} minutes`
                : t("Fleet.Settings.General.None")
            }
            tooltip={t("Fleet.Settings.General.CurfewTooltip")}
            labelMinWidth="25%"
          />
        </Grid>
        <Grid item xs={12}>
          <KeyValueDisplay
            label={t("Fleet.Settings.General.MinimumAge")}
            text={
              activeFleet?.minimum_age
                ? activeFleet.minimum_age.toString()
                : t("Fleet.Settings.General.None")
            }
            labelMinWidth="25%"
          />
        </Grid>
        <Grid item xs={12}>
          <KeyValueDisplay
            label={t("Fleet.Settings.General.ProofOfAge")}
            text={
              activeFleet?.minimum_age
                ? t(`Fleet.Settings.General.${activeFleet?.required_jumio_level}`)
                : t("Fleet.Settings.General.None")
            }
            labelMinWidth="25%"
            noWrap={false}
          />
        </Grid>
        <Grid item xs={12}>
          <KeyValueDisplay
            label={t("Fleet.Settings.General.FirstRideSpeedLimit")}
            text={
              !activeFleet?.slow_first_ride || !activeFleet?.first_ride_speed
                ? t("Fleet.Settings.General.None")
                : `${activeFleet?.first_ride_speed} KM/H`
            }
            labelMinWidth="25%"
          />
        </Grid>
        <Grid item xs={12}>
          <KeyValueDisplay
            label={t("Fleet.Settings.General.ReservationLimit")}
            text={
              activeFleet?.deployment_reservation_limit
                ? activeFleet.deployment_reservation_limit.toString()
                : t("Fleet.Settings.General.None")
            }
            tooltip={t("Fleet.Settings.General.ReservationLimitTooltip")}
            labelMinWidth="25%"
          />
        </Grid>
        <Grid item xs={12}>
          <KeyValueDisplay
            label={t("Fleet.Settings.General.NotificationEmail")}
            text={activeFleet?.notification_email || t("Fleet.Settings.General.None")}
            labelMinWidth="25%"
          />
        </Grid>
        <Grid item xs={12}>
          <KeyValueDisplay
            label={t("Fleet.Settings.General.BookAhead")}
            text={
              activeFleet?.is_bookable
                ? t("Fleet.Settings.General.Enabled")
                : t("Fleet.Settings.General.Disabled")
            }
            labelMinWidth="25%"
          />
        </Grid>
        <Grid item xs={12}>
          <KeyValueDisplay
            label={t("Fleet.Settings.General.max_active_reservations_per_group")}
            text={activeFleet?.max_active_reservations_per_group || "0"}
            labelMinWidth="25%"
          />
        </Grid>
        <Grid item xs={12}>
          <KeyValueDisplay
            label={t("Fleet.Settings.General.max_total_reservations_per_group")}
            text={activeFleet?.max_total_reservations_per_group || "0"}
            labelMinWidth="25%"
          />
        </Grid>
      </FleetSettingsSection>

      {/* Begin Customer Communications Section */}
      <FleetSettingsSection
        heading={t("Fleet.Settings.General.CustomerCommunications")}
        item
        container
        spacing={2}
      >
        <Grid item xs={12}>
          <KeyValueDisplay
            label={t("Fleet.Settings.General.RiderSafetyQuiz")}
            text={getRiderSafetyQuizStr()}
            tooltip={t("Fleet.Settings.General.RiderSafetyQuizTooltip")}
            textLinkTo={
              activeFleet?.required_survey_template_uuid
                ? `${window.REACT_APP_SURVEY_URL}/${activeFleet.required_survey_template_uuid}`
                : null
            }
            textLinkExternal
            textColor={activeFleet?.required_survey_template_uuid ? "textSecondary" : "textPrimary"}
            labelMinWidth="25%"
            noWrap={false}
          />
        </Grid>
        {activeFleet.survey_enabled ? (
          <Grid item xs={12}>
            <KeyValueDisplay
              label={t("Fleet.Settings.General.FirstSurveyOffset")}
              text={getFirstSurveyOffsetString()}
              tooltip={t("Fleet.Settings.General.FirstSurveyOffsetToolTip")}
              textColor={
                activeFleet?.required_survey_template_uuid ? "textSecondary" : "textPrimary"
              }
              labelMinWidth="25%"
              noWrap={false}
            />
          </Grid>
        ) : null}
        <Grid item xs={12}>
          <KeyValueDisplay
            label={t("Fleet.Settings.General.SidewalkToastMessage")}
            text={
              activeFleet?.enable_sidewalk_notifications
                ? t("Fleet.Settings.General.Enabled")
                : t("Fleet.Settings.General.Disabled")
            }
            labelMinWidth="25%"
          />
        </Grid>
        <Grid item xs={12}>
          <KeyValueDisplay
            label={t("Fleet.Settings.General.ParkingPhotos")}
            text={
              activeFleet?.require_parking_photo
                ? t("Fleet.Settings.General.Required")
                : t("Fleet.Settings.General.NotRequired")
            }
            labelMinWidth="25%"
          />
        </Grid>
      </FleetSettingsSection>

      {/* Begin Vehicles Section */}
      <FleetSettingsSection
        heading={t("Fleet.Settings.General.Vehicles")}
        item
        container
        spacing={2}
      >
        <Grid item xs={12}>
          <KeyValueDisplay
            label={t("Fleet.Settings.General.CriticalBatteryThreshold")}
            text={`${activeFleet?.critical_battery_threshold}%`}
            labelMinWidth="25%"
          />
        </Grid>
        <Grid
          item
          xs={12}
          data-id={StringUtils.uuidFromUrl(activeFleet.default_firmware_package || "")}
        >
          <KeyValueDisplay
            label={t("Fleet.Settings.General.DefaultFirmwarePackage")}
            text={getDefaultFirmwarePackageStr()}
            labelMinWidth="25%"
            noWrap={false}
          />
        </Grid>
        <Grid item xs={12}>
          <KeyValueDisplay
            label={t("Fleet.Settings.General.DefaultGeofencePackage")}
            text={getDefaultGeofencePackageStr()}
            labelMinWidth="25%"
            noWrap={false}
          />
        </Grid>
        <PermissionedDisplay permission={Permissions.feature.lockTo}>
          <Grid item xs={12}>
            <KeyValueDisplay
              label={t("Fleet.Settings.General.LockTo")}
              text={
                activeFleet?.lock_to_enabled
                  ? t("Fleet.Settings.General.Required")
                  : t("Fleet.Settings.General.NotRequired")
              }
              labelMinWidth="25%"
            />
          </Grid>
        </PermissionedDisplay>
      </FleetSettingsSection>
    </Grid>
  );
};

export default SettingsGeneral;
